import { useEffect, useState } from "react";

// Placeholder tones. Muted enough to sit behind text in a dense table.
const TONES = [
  "bg-rose-100 text-rose-600",
  "bg-sky-100 text-sky-600",
  "bg-amber-100 text-amber-600",
  "bg-emerald-100 text-emerald-600",
  "bg-violet-100 text-violet-600",
  "bg-cyan-100 text-cyan-600",
];

const SIZES = {
  sm: "w-9 h-9 text-[11px]",
  md: "w-11 h-11 text-sm",
  lg: "w-16 h-16 text-lg",
};

/** "Adam Levine" -> AL, "adam.levine@acme.com" -> AL, nothing usable -> ?. */
const initialsOf = (name, email) => {
  const source = (name || "").trim() || (email || "").split("@")[0] || "";
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

/** Same vendor always gets the same tone, so rows stay recognisable. */
const toneOf = (seed = "") => {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) | 0;
  }
  return TONES[Math.abs(hash) % TONES.length];
};

/**
 * Vendor profile picture with an initials placeholder for vendors that never
 * uploaded one — and for the ones whose image fails to load, which is why the
 * fallback is state and not just a `src` check.
 */
const VendorAvatar = ({ src, name, email, size = "md" }) => {
  const [failed, setFailed] = useState(false);

  // Avatars start on the stored S3 URL and switch to a presigned one once it
  // arrives; a failure against the first URL must not stick to the second.
  useEffect(() => setFailed(false), [src]);

  const dimensions = SIZES[size] || SIZES.md;

  if (!src || failed) {
    return (
      <div
        className={`${dimensions} ${toneOf(email || name || "")} shrink-0 rounded-full flex items-center justify-center font-semibold select-none`}
        title={name || email}
      >
        {initialsOf(name, email)}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name || email || "Vendor"}
      title={name || email}
      onError={() => setFailed(true)}
      className={`${dimensions} shrink-0 rounded-full object-cover bg-gray-100 ring-1 ring-gray-200`}
    />
  );
};

export default VendorAvatar;
